import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, useTheme, CircularProgress, Dialog, DialogContent, DialogActions, FormControl, Select, MenuItem, Menu } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import BaseCard from "../../../components/Cards/Card";
import CustomText, { textStyles } from "../../../components/CustomText";
import CustomButton from "../../../components/CustomButton";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-hot-toast";
import { BASE_URL } from "../../../components/BaseUrl";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import dummy from "../../../assets/dummy.png";
import save from "../../../assets/save.svg";
import deleteicon from "../../../assets/Settings/deleteicon.svg";
import Location from "../../AdminPages/Settings/Location";
import countryList from "react-select-country-list";
import { Country, State, City } from "country-state-city";
import SearchableDropdown from "../../../components/SearchableComponent";
import Webcam from "react-webcam";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import document1 from "../../../assets/document1.svg";
import imageicon from "../../../assets/imageicon.png";

const UpdateInviteProfile = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Get password_token and email from previous screen
  const passwordToken = localStorage.getItem("workerPasswordToken");
  const email = location.state?.email || localStorage.getItem("inviteEmail");
  const workerId = location.state?.workerId; // Get worker_id from navigation state


  const [savingProfile, setSavingProfile] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Profile Image handling
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);
  const [profileImageRaw, setProfileImageRaw] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [imageMenuAnchor, setImageMenuAnchor] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [takingPhoto, setTakingPhoto] = useState(false);

  // Location & Address handling
  const [manualAddress, setManualAddress] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [displayAddress, setDisplayAddress] = useState("");



const [documentUrl, setDocumentUrl] = useState("");
const [documentType, setDocumentType] = useState(null); // "pdf" | "image"
const [documentFileName, setDocumentFileName] = useState("");


  const buildDisplayUrl = (raw) => {
    if (!raw) return dummy;
    if (raw.startsWith("data:")) return raw;
    if (/^https?:\/\//i.test(raw)) return raw;
    const prefix = raw.startsWith("/") ? "" : "/";
    return `${BASE_URL}${prefix}${raw}`;
  };

  const extractUploadUrl = (data) =>
    data?.url ||
    data?.imageUrl ||
    data?.path ||
    data?.filePath ||
    data?.location ||
    data?.result?.url ||
    data?.result?.path ||
    data?.data?.url ||
    data?.data?.path ||
    data?.data?.location ||
    "";

  const handleImageSelect = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Revoke old blob URL to prevent memory leaks
    if (previewUrl && previewUrl.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    // Create new local preview immediately
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    try {
      const formData = new FormData();
      formData.append("image", file);

      const res = await fetch(`${BASE_URL}/upload/image`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      console.log("📤 Upload response:", data);

      if (!res.ok) throw new Error(data?.message || "Upload failed");

      const serverUrl = extractUploadUrl(data);
      console.log("🔗 Extracted server URL:", serverUrl);

      if (serverUrl) {
        setProfileImageRaw(serverUrl);
        // Keep the local preview URL for display (avoids CORS issues)
        // The serverUrl will be sent to backend, but we display the blob
        toast.success(t("updateInviteProfile.toast.imageUploadSuccess"));
      } else {
        throw new Error("No URL returned from server");
      }
    } catch (err) {
      console.error("❌ Upload error:", err);
      toast.error(err.message || t("updateInviteProfile.toast.imageUploadError"));
      // Keep the preview URL on error
    }
  };

  const openCamera = async () => {
    setImageMenuAnchor(null);
    
    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error(t("updateInviteProfile.toast.cameraNotSupported") || "Camera is not supported in this browser.");
      return;
    }

    // Check if we're on HTTPS (required for camera access in most browsers)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      toast.error(t("updateInviteProfile.toast.httpsRequired") || "Camera access requires HTTPS. Please use a secure connection.");
      return;
    }

    try {
      // Check permission state first (if Permissions API is available)
      let permissionStatus = null;
      if (navigator.permissions && navigator.permissions.query) {
        try {
          permissionStatus = await navigator.permissions.query({ name: 'camera' });
          console.log("Camera permission state:", permissionStatus.state);
          
          // If permission was previously denied, show helpful message
          if (permissionStatus.state === 'denied') {
            toast.error(t("updateInviteProfile.toast.cameraPermissionBlocked") || "Camera permission is blocked. Please enable it in your browser settings and refresh the page.");
            return;
          }
        } catch (permError) {
          console.log("Permissions API not fully supported, proceeding with getUserMedia");
        }
      }

      // Request camera permission - this will show the browser popup
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: "user",
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      // Permission granted - stop the stream and open camera modal
      stream.getTracks().forEach(track => track.stop());
      setCapturedImage(null);
      setShowCamera(true);
    } catch (error) {
      console.error("Camera permission error:", error);
      let errorMessage = t("updateInviteProfile.toast.cameraPermissionDenied") || "Camera permission denied";
      
      if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
        errorMessage = t("updateInviteProfile.toast.cameraPermissionDenied") || "Camera permission was denied. Please click 'Allow' when the browser asks for permission.";
      } else if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        errorMessage = t("updateInviteProfile.toast.cameraNotFound") || "No camera found on this device.";
      } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
        errorMessage = t("updateInviteProfile.toast.cameraInUse") || "Camera is being used by another application.";
      } else if (error.name === "OverconstrainedError" || error.name === "ConstraintNotSatisfiedError") {
        errorMessage = t("updateInviteProfile.toast.cameraConstraintError") || "Camera constraints could not be satisfied.";
      } else {
        errorMessage = error.message || t("updateInviteProfile.toast.cameraError") || "Failed to access camera. Please check permissions.";
      }
      
      toast.error(errorMessage);
    }
  };

  const capturePhoto = async () => {
    if (!webcamRef.current) {
      toast.error(t("updateInviteProfile.toast.cameraNotReady") || "Camera is not ready");
      return;
    }

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      toast.error(t("updateInviteProfile.toast.failedToCapture") || "Failed to capture image");
      return;
    }

    setCapturedImage(imageSrc);
    setTakingPhoto(true);

    try {
      // Convert base64 to blob
      const blob = await fetch(imageSrc).then((res) => res.blob());
      const formData = new FormData();
      formData.append("image", blob, "profile.jpg");

      const res = await fetch(`${BASE_URL}/upload/image`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      console.log("📤 Upload response:", data);

      if (!res.ok) throw new Error(data?.message || "Upload failed");

      const serverUrl = extractUploadUrl(data);
      console.log("🔗 Extracted server URL:", serverUrl);

      if (serverUrl) {
        setProfileImageRaw(serverUrl);
        setPreviewUrl(imageSrc); // Use captured image as preview
        toast.success(t("updateInviteProfile.toast.imageUploadSuccess") || "Photo captured & uploaded!");
        setShowCamera(false);
        setCapturedImage(null);
      } else {
        throw new Error("No URL returned from server");
      }
    } catch (err) {
      console.error("❌ Upload error:", err);
      toast.error(err.message || t("updateInviteProfile.toast.imageUploadError"));
    } finally {
      setTakingPhoto(false);
    }
  };

  // Calculate image source - this recalculates on every render
  const getImageSrc = () => {
    if (previewUrl) return previewUrl;
    if (profileImageRaw) return buildDisplayUrl(profileImageRaw);
    return dummy;
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
const [countriesData, setCountriesData] = useState([]);
const [statesList, setStatesList] = useState([]);
const [citiesList, setCitiesList] = useState([]);

const [selectedCountry, setSelectedCountry] = useState("");
const [selectedCountryIso, setSelectedCountryIso] = useState("");

const [selectedState, setSelectedState] = useState("");
const [selectedStateIso, setSelectedStateIso] = useState("");

const [selectedCity, setSelectedCity] = useState("");
useEffect(() => {
  const list = Country.getAllCountries().map((c) => ({
    name: c.name,
    isoCode: c.isoCode,
  }));

  setCountriesData(list);
  setCountries(list.map((c) => c.name));  // ADD THIS LINE
}, []);

const handleCountryChange = (value) => {
  setSelectedCountry(value);
  setSelectedState("");
  setSelectedCity("");
  setStatesList([]);
  setCitiesList([]);

  const countryObj = countriesData.find((c) => c.name === value);

  if (countryObj) {
    setSelectedCountryIso(countryObj.isoCode);

    const states = State.getStatesOfCountry(countryObj.isoCode).map((s) => ({
      name: s.name,
      isoCode: s.isoCode,
    }));

    setStatesList(states);
  }

  formik.setFieldValue("country", value);
};


const handleStateChange = (value) => {
  setSelectedState(value);
  setSelectedCity("");
  setCitiesList([]);

  formik.setFieldValue("province", value);

  const stateObj = statesList.find((s) => s.name === value);

  if (stateObj) {
    setSelectedStateIso(stateObj.isoCode);

    const cities = City.getCitiesOfState(selectedCountryIso, stateObj.isoCode).map(
      (c) => c.name
    );

    setCitiesList(cities);
  }
};



  const geocodeSelection = async (country, state, city, setFieldValue) => {
    try {
      const address = `${city}, ${state}, ${country}`;
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`);
      const data = await res.json();
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        setFieldValue("latitude", parseFloat(lat));
        setFieldValue("longitude", parseFloat(lon));
      }
    } catch {
      console.log("Geocoding failed");
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: email || "",
      dob: "",
      phone: "",
      designation: "",
      employeeType: "",
      hireDate: "",
      shiftSchedule: "",
      salary: "",
      workHours: "",
      streetAddress: "",
      city: "",
      province: "",
      country: "",
      postalCode: "",
      latitude: null,
      longitude: null,
    },
    validate: (values) => {
      const errors = {};
      if (!values.firstName?.trim()) errors.firstName = t("updateInviteProfile.validation.firstNameRequired");
      if (!values.lastName?.trim()) errors.lastName = t("updateInviteProfile.validation.lastNameRequired");
      if (!values.phone) errors.phone = t("updateInviteProfile.validation.phoneRequired");
      if (!values.dob) errors.dob = t("updateInviteProfile.validation.dobRequired");
      return errors;
    },
    onSubmit: async (values) => {
      if (!passwordToken) {
        toast.error(t("updateInviteProfile.toast.missingToken"));
        return;
      }

      try {
        setSavingProfile(true);

        const payload = {
          email: values.email,
          password_token: passwordToken,
          first_name: values.firstName,
          middle_name: values.middleName,
          last_name: values.lastName,
          dob: values.dob,
          phone: values.phone,
          profile_image: profileImageRaw || "",
          designation: values.designation,
          employee_type: values.employeeType,
          hire_date: values.hireDate,
          shift_schedule: values.shiftSchedule,
          salary: values.salary,
          work_hours: values.workHours,
          street_address: values.streetAddress,
          city: values.city,
          province: values.province,
          country: values.country,
          postal_code: values.postalCode,
          latitude: values.latitude,
          longitude: values.longitude,
        };

        console.log("📤 Sending profile update payload:", payload);

        const response = await fetch(`${BASE_URL}/public/worker/update-profile-with-token`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            // Authorization: `Bearer ${passwordToken}`,

          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        console.log("📩 Profile update response:", data);
        console.log("📩 Full response data:", JSON.stringify(data, null, 2));

        if (!response.ok || data.error) {
          throw new Error(data.message || t("updateInviteProfile.toast.profileUpdateError"));
        }
        toast.success(data.message || t("updateInviteProfile.toast.profileUpdateSuccess"));

 
        setTimeout(() => {
          // Get worker_id from localStorage (saved by VerifyInvite or this component)
          const storedWorkerId = localStorage.getItem("currentWorkerId") || localStorage.getItem("workerId");

          console.log("🔄 Navigating to face scan with worker_id:", storedWorkerId);
          console.log(storedWorkerId)
          navigate("/face-scan-registration", {
            state: { email: values.email, workerId: storedWorkerId },
            replace: true
          });
        }, 1200);
      } catch (err) {
        console.error("❌ Profile update error:", err);
        toast.error(err.message || t("updateInviteProfile.toast.profileUpdateError"));
      } finally {
        setSavingProfile(false);
      }
    },
  });

 const handleCityChange = async (value) => {
  setSelectedCity(value);
  formik.setFieldValue("city", value);

  await geocodeSelection(selectedCountry, selectedState, value, formik.setFieldValue);
};

  // const handleManualToggle = (e) => {
  //   const checked = e.target.checked;
  //   setManualAddress(checked);
  //   if (!checked) {
  //     setSelectedCountry("");
  //     setSelectedState("");
  //     setSelectedCity("");
     
  //   }
  // };

 const handleManualToggle = async (e) => {
  const checked = e.target.checked;
  setManualAddress(checked);

  if (
    checked &&
    typeof formik.values.latitude === "number" &&
    typeof formik.values.longitude === "number"
  ) {
    const {
      country,
      province,
      city,
      postalCode,
      streetAddress,
    } = await reverseGeocodeFromLatLng(
      formik.values.latitude,
      formik.values.longitude
    );

    // Formik values
    formik.setFieldValue("country", country);
    formik.setFieldValue("province", province);
    formik.setFieldValue("city", city);
    formik.setFieldValue("postalCode", postalCode);
    formik.setFieldValue("streetAddress", streetAddress);

    // Manual dropdown UI state
    setSelectedCountry(country);
    setSelectedState(province);
    setSelectedCity(city);

    // Load dependent state list
    const countryObj = countriesData.find(c => c.name === country);
    if (countryObj) {
      setSelectedCountryIso(countryObj.isoCode);
      const states = State.getStatesOfCountry(countryObj.isoCode).map(s => ({
        name: s.name,
        isoCode: s.isoCode,
      }));
      setStatesList(states);
    }
  }
};


  
const reverseGeocodeFromLatLng = async (lat, lng) => {
  const res = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
  );
  const data = await res.json();
  const addr = data?.address || {};

  return {
    country: addr.country || "",
    province: addr.state || "",
    city: addr.city || addr.town || addr.village || "",
    postalCode: addr.postcode || "",
    streetAddress: [addr.road, addr.house_number, addr.neighbourhood]
      .filter(Boolean)
      .join(", "),
  };
};


const handleMapLocation = useCallback((loc) => {
  if (!loc?.latitude || !loc?.longitude) return;

  // Always update coordinates
  formik.setFieldValue("latitude", loc.latitude);
  formik.setFieldValue("longitude", loc.longitude);

  // Only update address fields IF manual mode is ON
  if (manualAddress) {
    if (loc.city) formik.setFieldValue("city", loc.city);
    if (loc.province) formik.setFieldValue("province", loc.province);
    if (loc.country) formik.setFieldValue("country", loc.country);
    if (loc.postalCode) formik.setFieldValue("postalCode", loc.postalCode);
    if (loc.streetAddress) formik.setFieldValue("streetAddress", loc.streetAddress);
  }

  // Display address is map-only
  if (loc.address) {
    setDisplayAddress(loc.address);
  }
}, [manualAddress]);
  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate("/", { replace: true });
  };

    useEffect(() => {
  if (!workerId) return;

  const fetchWorker = async () => {
    try {
      const res = await fetch(`${BASE_URL}/public/worker/profile/${workerId}`);
      const data = await res.json();
        console.log("📥 Fetched worker profile data:", data);
      if (!res.ok || data.error) {
        toast.error(data.message || "Failed to load worker profile");
        return;
      }
const w = data.data.profile;



if (w.document_url) {
  setDocumentUrl(w.document_url);

  const lower = w.document_url.toLowerCase();

  if (lower.endsWith(".pdf")) {
    setDocumentType("pdf");
  } else {
    setDocumentType("image");
  }

  // Extract filename from URL
  setDocumentFileName(w.document_url.split("/").pop());
}

// Set Profile Image
if (w.profile_image) {
  console.log("🔵 Setting profileImageRaw to:", w.profile_image);
  setProfileImageRaw(w.profile_image);
} else {
  console.log("⚠️ No profile_image found in API response");
}

// Set Form Values
formik.setValues({
  firstName: w.first_name || "",
  middleName: w.middle_name || "",
  lastName: w.last_name || "",
  email: w.email || email || "",
  dob: w.dob ? w.dob.split("T")[0] : "",
  phone: w.phone || "",
  designation: w.designation || "",
  employeeType: w.employee_type || "",
  hireDate: w.hire_date ? w.hire_date.split("T")[0] : "",
  shiftSchedule: w.shift_schedule || "",
  salary: w.salary || "",
  workHours: w.work_hours || "",
  streetAddress: w.street_address || "",
  city: w.city || "",
  province: w.province || "",
  country: w.country || "",
  postalCode: w.postal_code || "",
  latitude: w.latitude ? Number(w.latitude) : null,
  longitude: w.longitude ? Number(w.longitude) : null,
});

      // Pre-select dropdowns (ONLY if manual address mode)
      // Check if we have address data - if so, enable manual address mode
      if (w.country || w.province || w.city || w.street_address || w.postal_code) {
        // setManualAddress(true);
        setSelectedCountry(w.country || "");
        setSelectedState(w.province || "");
        setSelectedCity(w.city || "");

        // Wait for countriesData to be loaded before setting states/cities
        if (countriesData && countriesData.length > 0) {
          // load states
          const countryObj = countriesData.find((c) => c.name === w.country);
          if (countryObj) {
            const states = State.getStatesOfCountry(countryObj.isoCode).map((s) => ({
              name: s.name,
              isoCode: s.isoCode,
            }));
            setStatesList(states);
            setSelectedCountryIso(countryObj.isoCode);

            const stateObj = states.find((s) => s.name === w.province);
            if (stateObj) {
              setSelectedStateIso(stateObj.isoCode);
              const cities = City.getCitiesOfState(
                countryObj.isoCode,
                stateObj.isoCode
              ).map((c) => c.name);
              setCitiesList(cities);
            }
          }
        }
      } else if (w.latitude && w.longitude) {
        // If we have lat/long but no manual address, use map mode
        setManualAddress(false);
      }

      // Set initial display address from API data
      if (w.street_address || w.city || w.province || w.country) {
        const addressParts = [
          w.street_address,
          w.city,
          w.province,
          w.postal_code,
          w.country
        ].filter(Boolean);
        setDisplayAddress(addressParts.join(", "));
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load worker data");
    }
  };

  fetchWorker();
}, [workerId, countriesData]);

  return (
    <Box sx={{ py: isMobile ? 5 : 8, px: 2, mt: isMobile ? 2 : 0, backgroundColor: "#EFF4F8", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <BaseCard maxWidth={isMobile ? "100%" : 700}>
          {/* Progress Indicator */}
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  color: "#fff",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                1
              </Box>
              <Box sx={{ width: isMobile ? 50 : 95, height: 2, backgroundColor: "#091E4224" }} />
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  borderRadius: "50%",
                  backgroundColor: "#ffffff",
                  color: "#BCBFC2",
                  border: "2px solid #091E4224",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                2
              </Box>
            </Box>
          </Box>

          <CustomText
            sx={{
              ...textStyles.h4,
              color: theme.palette.tertiary.main,
              textAlign: "left",
              fontSize: "26px",
              mb: 1,
            }}
          >
            {t("updateInviteProfile.title") || "Complete Your Profile"}
          </CustomText>
          <CustomText
            sx={{
              ...textStyles.body5,
              color: theme.palette.text.primary,
              textAlign: "left",
              mb: 3,
            }}
          >
            {t("updateInviteProfile.subtitle") || "Please provide your details to complete registration"}
          </CustomText>

          {/* Form */}
          <form onSubmit={formik.handleSubmit}>
            {/* Profile Image */}
            <Box sx={{ mb: 3 }}>
              <label className="field_label">{t("account_setting_screen.imageLabel") || "Profile Image"}</label>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, mt: 1 }}>
               
<Box sx={{ position: "relative", width: 100, height: 100 }}>
  <img
    src={getImageSrc()}
    alt="profile"
    crossOrigin="anonymous"
    style={{
      width: 100,
      height: 100,
      borderRadius: "50%",
      objectFit: "cover",
    }}
    onError={(e) => {
      e.currentTarget.src = dummy;
    }}
  />

  {/* PLUS ICON */}
  <IconButton
    size="small"
    sx={{
      position: "absolute",
      top: -6,
      right: -6,
      backgroundColor: "#fff",
      boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
      "&:hover": { backgroundColor: "#f5f5f5" },
    }}
    onClick={(e) => setImageMenuAnchor(e.currentTarget)}
  >
    <AddIcon fontSize="small" />
  </IconButton>

  <input
    type="file"
    accept="image/*"
    ref={fileInputRef}
    style={{ display: "none" }}
    onChange={handleImageSelect}
  />
</Box>
                <Box sx={{ display: "flex", gap: 1 }}>
              
                  <Menu
                    anchorEl={imageMenuAnchor}
                    open={Boolean(imageMenuAnchor)}
                    onClose={() => setImageMenuAnchor(null)}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                  >
                    <MenuItem
                      onClick={openCamera}
                      sx={{ fontSize: "14px", fontFamily: "Poppins, sans-serif" }}
                    >
                      {t("account_setting_screen.takePhoto") || "Take Photo"}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        fileInputRef.current?.click();
                        setImageMenuAnchor(null);
                      }}
                      sx={{ fontSize: "14px", fontFamily: "Poppins, sans-serif" }}
                    >
                      {t("account_setting_screen.chooseFromGallery") || "Choose from Gallery"}
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            </Box>

            {/* Name Fields */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <label className="field_label">
                  {t("worker_settings.firstName") || "First Name"} <span className="red_asterisk">*</span>
                </label>
                <input
                  className="project_input"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  style={{ width: "100%", height: 40 }}
                />
                {formik.errors.firstName && formik.touched.firstName && (
                  <CustomText sx={{ color: "red", fontSize: 12, mt: 0.5 }}>{formik.errors.firstName}</CustomText>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                <label className="field_label">{t("worker_settings.middleName") || "Middle Name"}</label>
                <input
                  className="project_input"
                  name="middleName"
                  value={formik.values.middleName}
                  onChange={formik.handleChange}
                  style={{ width: "100%", height: 40 }}
                />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <label className="field_label">
                {t("worker_settings.lastName") || "Last Name"} <span className="red_asterisk">*</span>
              </label>
              <input
                className="project_input"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                style={{ width: "100%", height: 40 }}
              />
              {formik.errors.lastName && formik.touched.lastName && (
                <CustomText sx={{ color: "red", fontSize: 12, mt: 0.5 }}>{formik.errors.lastName}</CustomText>
              )}
            </Box>

            {/* Email (disabled) */}
            <Box sx={{ mb: 2 }}>
              <label className="field_label">{t("worker_settings.email") || "Email"}</label>
              <input
                className="project_input"
                name="email"
                value={formik.values.email}
                disabled
                style={{ width: "100%", height: 40, backgroundColor: "#f5f5f5" }}
              />
            </Box>

            {/* Date of Birth */}
            <Box sx={{ mb: 2 }}>
              <label className="field_label">
                {t("worker_settings.dob") || "Date of Birth"} <span className="red_asterisk">*</span>
              </label>
              <input
                type="date"
                className="project_input"
                name="dob"
                value={formik.values.dob}
                // onChange={formik.handleChange}
                onChange={(e) => {
  const selected = e.target.value;
  const birthDate = new Date(selected);
  const today = new Date();

  const age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();

  const isUnder18 = age < 18 || (age === 18 && m < 0);

  if (isUnder18) {
    toast.error(t("age")); // add t() if needed
    return; // stop updating formik
  }

  formik.handleChange(e); // update value only if valid
}}

                style={{ width: "100%", height: 40 }}
              />
              {formik.errors.dob && formik.touched.dob && (
                <CustomText sx={{ color: "red", fontSize: 12, mt: 0.5 }}>{formik.errors.dob}</CustomText>
              )}
            </Box>

            {/* Phone */}
            <Box sx={{ mb: 2 }}>
              <label className="field_label">
                {t("account_setting_screen.phoneLabel") || "Phone"} <span className="red_asterisk">*</span>
              </label>
              <Box
                sx={{
                  border: "2px solid rgba(9,30,66,0.14)",
                  borderRadius: "6px",
                  backgroundColor: "white",
                  "& .form-control": {
                    border: "none !important",
                    outline: "none !important",
                    boxShadow: "none !important",
                    width: "100%",
                    height: "36px",
                    fontSize: "14px",
                    paddingLeft: "48px",
                    borderRadius: "6px",
                    fontFamily: "Poppins, sans-serif !important",
                    backgroundColor: "transparent",
                  },
                  "& .flag-dropdown": {
                    border: "none !important",
                    borderRight: "1px solid #ccc !important",
                    backgroundColor: "#f9f9f9",
                    borderTopLeftRadius: "6px",
                    borderBottomLeftRadius: "6px",
                  },
                }}
              >
                <PhoneInput
                country={"us"}
                  countryCodeEditable={false}
                  value={formik.values.phone}
                  onChange={(value) => formik.setFieldValue("phone", value)}
                  inputProps={{ name: "phone", required: true }}
                />
              </Box>
              {formik.errors.phone && formik.touched.phone && (
                <CustomText sx={{ color: "red", fontSize: 12, mt: 0.5 }}>{formik.errors.phone}</CustomText>
              )}
            </Box>

            {/* Job Details */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <label className="field_label">{t("worker_settings.designation") || "Designation"}</label>
                <input
                  className="project_input"
                  name="designation"
                    disabled
                  value={formik.values.designation}
                  onChange={formik.handleChange}
                  style={{ width: "100%", height: 40 }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                {/* <label className="field_label">{t("worker_settings.employeeType") || "Employee Type"}</label>
                <input
                  className="project_input"
                  name="employeeType"
                  value={formik.values.employeeType}
                  onChange={formik.handleChange}
                  style={{ width: "100%", height: 40 }}
                /> */}
                <label className="field_label">{t("worker_settings.employeeType") || "Employee Type"}</label>

<FormControl fullWidth size="small">
  <Select
    disabled
    value={formik.values.employeeType}
    onChange={(e) => formik.setFieldValue("employeeType", e.target.value)}
    displayEmpty
    sx={{
      height:38,
      borderRadius: "6px",
      fontFamily: "Poppins, sans-serif",
      fontSize: "14px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#DFE1E6", borderWidth: "2px" },
      "&:hover fieldset": { borderColor: "#1976d2" },
      "&.Mui-focused fieldset": { borderColor: "#1976d2" },
    }}
  >
      <MenuItem value="Full-Time" sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
           {t("empFullTime")}
         </MenuItem>
   
         <MenuItem value="Part-Time" sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
           {t("empPartTime")}
         </MenuItem>
   
         <MenuItem value="Intern" sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
           {t("empIntern")}
         </MenuItem>
   
         <MenuItem value="Contract" sx={{ fontSize: "13px", fontFamily: "Poppins, sans-serif" }}>
           {t("empContract")}
         </MenuItem>
  </Select>
</FormControl>
              </Box>
     

            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              {/* <Box sx={{ flex: 1 }}>
                <label className="field_label">{t("worker_settings.hireDate") || "Hire Date"}</label>
                <input
                  type="date"
                  className="project_input"
                  name="hireDate"
                  value={formik.values.hireDate}
                  onChange={formik.handleChange}
                  style={{ width: "100%", height: 40 }}
                />
              </Box> */}
              <Box sx={{ flex: 1 }}>
  <label className="field_label">
    {t("worker_settings.hireDate") || "Hire Date"}
  </label>

  <input
    type="date"
      disabled
    className="project_input"
    name="hireDate"
    value={formik.values.hireDate}
    onChange={(e) => {
      const val = e.target.value; // yyyy-mm-dd

      // Extract only the year part
      const year = val.split("-")[0];

      // Prevent more than 4 digits or non-numeric years
      if (year.length > 4) {
        return; // ❌ Do not update Formik
      }

      formik.handleChange(e); // ✅ Accept valid date
    }}
    style={{ width: "100%", height: 40 }}
  />
</Box>

              <Box sx={{ flex: 1 }}>
                {/* <label className="field_label">{t("worker_settings.shiftSchedule") || "Shift Schedule"}</label>
                <input
                  className="project_input"
                  name="shiftSchedule"
                  value={formik.values.shiftSchedule}
                  onChange={formik.handleChange}
                  style={{ width: "100%", height: 40 }}
                /> */}
                <label className="field_label">{t("worker_settings.shiftSchedule") || "Shift Schedule"}</label>

<FormControl fullWidth size="small">
  <Select
    value={formik.values.shiftSchedule}
    onChange={(e) => formik.setFieldValue("shiftSchedule", e.target.value)}
    displayEmpty
      disabled
    sx={{
      height: 38,
      borderRadius: "6px",
      fontFamily: "Poppins, sans-serif",
      fontSize: "14px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#DFE1E6", borderWidth: "2px" },
      "&:hover fieldset": { borderColor: "#1976d2" },
      "&.Mui-focused fieldset": { borderColor: "#1976d2" },
    }}
  >
  <MenuItem value="Morning" sx={{fontSize:"13px",fontFamily: 'Poppins, sans-serif',}}>{t("shiftMorning")}</MenuItem>
      <MenuItem value="Evening" sx={{fontSize:"13px",fontFamily: 'Poppins, sans-serif',}}>{t("shiftEvening")}</MenuItem>
      <MenuItem value="Night" sx={{fontSize:"13px",fontFamily: 'Poppins, sans-serif',}}> {t("shiftNight")}</MenuItem>
      <MenuItem value="Rotational" sx={{fontSize:"13px",fontFamily: 'Poppins, sans-serif',}}>{t("shiftRotational")}</MenuItem>
  </Select>
</FormControl>

              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <label className="field_label">{t("worker_settings.salary") || "Salary"}</label>
                <input
                 type="number"
                  className="project_input"
                  name="salary"
                    disabled
                  value={formik.values.salary}
                  onChange={formik.handleChange}
                  style={{ width: "100%", height: 40 }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <label className="field_label">{t("worker_settings.workHours") || "Work Hours"}</label>
                <input
                 type="number"
                  className="project_input"
                  name="workHours"
                    disabled
                  value={formik.values.workHours}
                  onChange={formik.handleChange}
                  style={{ width: "100%", height: 40 }}
                />
              </Box>
            </Box>





<Box sx={{ mb: 2, mt: 3 }}>
  <label
    style={{
      color: "#626F86",
      fontSize: 14,
      marginBottom: 7,
      display: "inline-block",
    }}
  >
    {t("settings.nationalId")}
  </label>

  <Box
    sx={{
      position: "relative",
      border: "2px dashed #091E4224",
      borderRadius: "6px",
      padding: "20px",
      textAlign: "center",
      backgroundColor: "#f9f9f9",
      cursor: "default",
    }}
  >
    {documentUrl ? (
      <Box sx={{ display: "inline-block" }}>
        <Box sx={{ mb: 1 }}>
          {documentType === "pdf" ? (
            <img src={document1} alt="PDF" width={30} />
          ) : (
            <img src={imageicon} alt="Image" width={30} />
          )}
        </Box>

        <span style={{ fontSize: 14, color: "#16a34a" }}>
          ✓ {documentFileName}
        </span>

        <br />

        <a
          href={documentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 12,
            color: "#006EC2",
            textDecoration: "underline",
            display: "inline-block",
            marginTop: 6,
          }}
        >
          {t("viewDocuments") || "View document"}
        </a>
      </Box>
    ) : (
      <span style={{ fontSize: 14, color: "#999" }}>
        {t("settings.noDocumentUploaded") || "No document uploaded"}
      </span>
    )}
  </Box>
</Box>


            {/* Address Fields */}
            <Box sx={{ mb: 3, mt: 3 }}>
              <CustomText sx={{ fontSize: 16, fontWeight: 600, color: "#2C384C", mb: 2 }}>
                {t("worker_settings.locationSection") || "Location & Address"}
              </CustomText>

              {/* Manual Address Toggle */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <input
                  type="checkbox"
                  id="manualAddress"
                  checked={manualAddress}
                  onChange={handleManualToggle}
                  style={{ width: 18, height: 18, cursor: "pointer" }}
                />
                <label htmlFor="manualAddress" style={{ cursor: "pointer", fontSize: 14, color: "#626F86" }}>
                  {t("worker_settings.manualAddress") || "Enter address manually"}
                </label>
              </Box>

              {!manualAddress ? (
  <Box sx={{ mb: 2 }}>
    {/* <Box sx={{ mb: 2 }}>
      <label className="field_label">{t("worker_settings.address") || "Address"}</label>
      <input
        className="project_input"
        value={displayAddress || ""}
        readOnly
        placeholder={t("worker_settings.addressPlaceholder") || "Address will appear here when you select a location on the map"}
        style={{ width: "100%", height: 40, backgroundColor: "#f5f5f5", cursor: "not-allowed" }}
      />
    </Box> */}
    <Location
      onChange={handleMapLocation}
      // initialLat={formik.values.latitude && !isNaN(formik.values.latitude) ? formik.values.latitude : 40.4168}
      // initialLng={formik.values.longitude && !isNaN(formik.values.longitude) ? formik.values.longitude : -3.7038}
    
       initialLat={formik.values.latitude}
  initialLng={formik.values.longitude}
    />
  </Box>
) : (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>

    {/* COUNTRY */}
    <Box>
      <label className="field_label">
        {t("worker_settings.country") || "Country"} <span className="red_asterisk">*</span>
      </label>

      <Box sx={{ position: "relative" }}>
        <SearchableDropdown
          value={selectedCountry}
          onChange={handleCountryChange}
          options={countriesData.map((c) => c.name)}
          placeholder={t("settings.country")}
        />

        {loadingStates && (
          <CircularProgress
            size={20}
            sx={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
        )}
      </Box>
    </Box>

    {/* STATE / PROVINCE */}
    <Box>
      <label className="field_label">
        {t("worker_settings.province") || "Province"} <span className="red_asterisk">*</span>
      </label>

      <Box sx={{ position: "relative", opacity: !selectedCountry ? 0.5 : 1 }}>
        <SearchableDropdown
          value={selectedState}
          onChange={handleStateChange}
          options={statesList.map((s) => s.name)}
          placeholder={t("settings.province")}
          disabled={!selectedCountry}
        />

        {loadingCities && (
          <CircularProgress
            size={20}
            sx={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
        )}
      </Box>
    </Box>

    {/* CITY */}
    <Box>
      <label className="field_label">
        {t("worker_settings.city") || "City"} <span className="red_asterisk">*</span>
      </label>

      <Box sx={{ position: "relative", opacity: !selectedState ? 0.5 : 1 }}>
        <SearchableDropdown
          value={selectedCity}
          onChange={handleCityChange}
          options={citiesList}
          placeholder={t("settings.city")}
          disabled={!selectedState}
        />
      </Box>
    </Box>
    
            {/* Street Address */}
            <Box sx={{ mb: 2 }}>
              <label className="field_label">{t("worker_settings.streetAddress") || "Street Address"}</label>
              <input
                className="project_input"
                name="streetAddress"
                value={formik.values.streetAddress}
                onChange={formik.handleChange}
                style={{ width: "100%", height: 40 }}
              />
            </Box>

            {/* Postal Code */}
            <Box sx={{ mb: 3 }}>
              <label className="field_label">{t("worker_settings.postalCode") || "Postal Code"}</label>
              <input
                className="project_input"
                name="postalCode"
                value={formik.values.postalCode}
                onChange={formik.handleChange}
                style={{ width: "100%", height: 40 }}
              />
            </Box>

  </Box>
)}

            </Box>

            {/* Submit Button */}
            <CustomButton
              type="submit"
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {savingProfile && <CircularProgress size={16} sx={{ color: "#fff" }} />}
                  <img src={save} alt="save" width={16} height={16} />
                  {t("worker_settings.save") || "Save & Continue"}
                </Box>
              }
              fullWidth
              size="medium"
            />
          </form>
        </BaseCard>
      </Box>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onClose={handleSuccessClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ textAlign: "center", py: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "#22c55e",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <span style={{ fontSize: 48, color: "white" }}>✓</span>
          </Box>
          <CustomText sx={{ ...textStyles.h4, color: theme.palette.tertiary.main, mb: 1 }}>
            {t("updateInviteProfile.successTitle") || "Profile Updated Successfully!"}
          </CustomText>
          <CustomText sx={{ ...textStyles.body4, color: theme.palette.text.primary }}>
            {t("updateInviteProfile.successMessage") || "You can now login with your credentials"}
          </CustomText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <CustomButton label={t("updateInviteProfile.goToLogin") || "Go to Login"} onClick={handleSuccessClose} />
        </DialogActions>
      </Dialog>

      {/* Camera Modal */}
      {showCamera && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1300,
            p: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "16px",
              padding: "24px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
              maxWidth: 550,
            }}
          >
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
              {!capturedImage ? (
                <Webcam
                  key={showCamera ? "webcam-active" : "webcam-inactive"}
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{
                    facingMode: "user",
                    width: 550,
                    height: 500
                  }}
                  style={{
                    borderRadius: "12px",
                    width: "100%",
                    maxWidth: "550px",
                    objectFit: "contain",
                    border: "2px solid #E0E0E0",
                  }}
                  onUserMediaError={(error) => {
                    console.error("Webcam error:", error);
                    toast.error(t("updateInviteProfile.toast.cameraError") || "Failed to access camera. Please check permissions.");
                  }}
                />
              ) : (
                <img
                  src={capturedImage}
                  alt="Captured"
                  style={{
                    borderRadius: "12px",
                    width: "100%",
                    maxWidth: "550px",
                    border: "2px solid #E0E0E0",
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mt: 3,
                width: "100%",
              }}
            >
              {!capturedImage ? (
                <CustomButton
                  label={takingPhoto ? (t("updateInviteProfile.uploading") || "Uploading...") : (t("updateInviteProfile.capture") || "Capture")}
                  onClick={capturePhoto}
                  disabled={takingPhoto}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <CustomButton
                  label={t("updateInviteProfile.retake") || "Retake"}
                  onClick={() => setCapturedImage(null)}
                  sx={{
                    flex: 1,
                    py: 1.2,
                    borderRadius: "8px",
                  }}
                />
              )}
              <CustomButton
                label={t("updateInviteProfile.close") || "Close"}
                onClick={() => {
                  setShowCamera(false);
                  setCapturedImage(null);
                }}
                sx={{
                  flex: 1,
                  py: 1.2,
                  borderRadius: "8px",
                  backgroundColor: "#E0E0E0",
                  color: "#333",
                  "&:hover": {
                    backgroundColor: "#D0D0D0",
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UpdateInviteProfile;
